import { ProcessingOverlay } from '@components'

import { BorderRadius, Color } from '@billy/design-system'
import { ReactElement, useEffect } from 'react'

import { useShouldDisplayExternalInvoicesSyncInitiateAction } from '@features/externalInvoicesSync/hooks/useShouldDisplayExternalInvoicesSyncInitiateAction'
import { useInvoiceComplianceMode } from '@features/invoices/hooks/useInvoiceComplianceMode'
import { useTrackInvoicingSettings } from '@features/invoicingSettings/hooks/useTrackInvoicingSettings'

import { useInvoiceSettingsForm } from '../../contexts/invoiceSettingsFormContext'
import { AppearanceSettings } from './elements/AppearanceSettings'
import { AutomaticPaymentReminders } from './elements/AutomaticPaymentReminders'
import { ContactSettings } from './elements/ContactSettings'
import { ContentSettings } from './elements/ContentSettings'
import { GeneralSettings } from './elements/GeneralSettings'
import { ImportSettings } from './elements/ImportSettings'
import { InvoiceSettingsFooter } from './elements/InvoiceSettingsFooter'
import { LegalMentionsSettings } from './elements/LegalMentionsSettings'
import { PaymentSettings } from './elements/PaymentSettings'
import { PreferencesSettings } from './elements/PreferencesSettings'
import { useAutomaticPaymentRemindersAccess } from './hooks/useAutomaticPaymentRemindersAccess'
import * as Styled from './styles'

export const InvoiceSettingsForm = (): ReactElement => {
  const { isFrenchComplianceMode } = useInvoiceComplianceMode()
  const { shouldDisplayAutomaticPaymentReminders, isAutomaticPaymentRemindersLockedByUpsell } =
    useAutomaticPaymentRemindersAccess()
  const { shouldDisplayExternalInvoicesSyncInitiateAction } = useShouldDisplayExternalInvoicesSyncInitiateAction()
  const { isLoading, isSubmitting } = useInvoiceSettingsForm()
  const { trackInvoicingSettings } = useTrackInvoicingSettings()

  const shouldDisplayLegalMentionsSettings = isFrenchComplianceMode

  useEffect(() => {
    trackInvoicingSettings('Tab opened')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <Styled.InvoiceSettingsFormWrapper>
      {(isLoading || isSubmitting) && (
        <ProcessingOverlay color={Color.White} borderRadius={BorderRadius.R16} withAnimation />
      )}
      <Styled.FormContentWrapper>
        <GeneralSettings />
        <AppearanceSettings />
        <ContentSettings />
        <ContactSettings />
        <PaymentSettings />
        {shouldDisplayExternalInvoicesSyncInitiateAction && <ImportSettings />}
        {shouldDisplayAutomaticPaymentReminders && (
          <AutomaticPaymentReminders isLockedByUpsell={isAutomaticPaymentRemindersLockedByUpsell} />
        )}
        {shouldDisplayLegalMentionsSettings && <LegalMentionsSettings />}
        <PreferencesSettings />
      </Styled.FormContentWrapper>
      <InvoiceSettingsFooter />
    </Styled.InvoiceSettingsFormWrapper>
  )
}
